import { PermissionKey, type PermissionMap } from "@fitness-app/data-models";

import {
  matchesPermissionRequirement,
  type PermissionCheck,
  type PermissionRequirement,
} from "~/modules/Auth/permissionRequirement";

const requiresClientPermission = (permission: PermissionCheck): PermissionRequirement => ({
  allOf: [{ permission: PermissionKey.CLIENTS_READ }, permission],
});

export const traineeFeaturePermissionRequirements = {
  results: { permission: PermissionKey.CLIENTS_READ },
  challengesHabits: requiresClientPermission({ permission: PermissionKey.ENGAGEMENT_MANAGE }),
  programs: requiresClientPermission({ permission: PermissionKey.PROGRAMS_MANAGE }),
  calendar: requiresClientPermission({ permission: PermissionKey.PROGRAMS_MANAGE }),
  nutrition: requiresClientPermission({ permission: PermissionKey.NUTRITION_MANAGE }),
  measurements: requiresClientPermission({ permission: PermissionKey.CLIENTS_WRITE }),
  surveys: requiresClientPermission({ permission: PermissionKey.SURVEYS_MANAGE }),
  notes: requiresClientPermission({ permission: PermissionKey.CLIENTS_WRITE }),
  products: { permission: PermissionKey.CLIENTS_READ },
  emails: { permission: PermissionKey.CLIENTS_READ },
  // Mirrors the generate endpoint + trainee_ai_report RLS: reports quote the
  // client's chat, so without chat.read the tab would be an empty list and a 403.
  aiReport: {
    allOf: [
      { permission: PermissionKey.CLIENTS_READ },
      { permission: PermissionKey.AGENTS_USE },
      { permission: PermissionKey.CHAT_READ },
    ],
  },
  settings: requiresClientPermission({ permission: PermissionKey.CLIENTS_WRITE }),
} as const satisfies Record<string, PermissionRequirement>;

export type TraineeFeatureKey = keyof typeof traineeFeaturePermissionRequirements;

export const isTraineeFeatureVisible = (
  key: TraineeFeatureKey,
  permissions: PermissionMap,
  challengesEnabled: boolean,
): boolean => {
  if (key === "challengesHabits" && !challengesEnabled) {
    return false;
  }

  return matchesPermissionRequirement(permissions, traineeFeaturePermissionRequirements[key]);
};
